import { mergeDeepRight } from 'ramda';

import { reduxActions } from './index';
import {
  createGrant,
  deleteGrant,
  deletePatientInformant,
  deletePatientContactInfo,
  fetchPatientContactInfo,
  fetchPatientData,
  fetchPatientDelegates,
  fetchPatientGrants,
  fetchPatientInformants,
  patchPatient,
  patchPatientInformant,
  patchPatientExitus,
  postPatientInformant,
  postPatientContactInfo,
  updateGrant,
  linkPatientToInformant,
  unlinkPatientFromInformant,
} from './apiFetchers';
import { TDispatch } from '..';

import store from 'Store/index';
import browserLanguage from 'Utility/browserLanguage';
import { getICD10Description } from 'Utility/randomUtil';
import { fetchMyUserContactInfo, fetchMyUserData } from 'Store/myapp/fetchers';

const getICD10DescriptionAndUpdateData = (data: IPatientUI, updateCallback: (data: IPatientUI) => void) => {
  const locale = store.getState().settings.userSettings.uiLanguage || browserLanguage;
  if (data.reasonOfDeath?.reasonCode?.code) {
    // Get description string for icd-10 code
    getICD10Description(data.reasonOfDeath.reasonCode?.code, locale)
      .then(d => {
        const updatedReasonCode = {
          ...data.reasonOfDeath?.reasonCode,
          icd10description: d,
        } as Partial<IICDReasonCode>;
        const updatedData = mergeDeepRight(data, { reasonOfDeath: { reasonCode: updatedReasonCode } }) as IPatientUI;
        updateCallback(updatedData);
      })
      .catch(() => {
        return;
      });
  } else updateCallback(data);
};

const loadPatientData =
  () =>
  (dispatch: TDispatch): void => {
    fetchPatientData()
      .then(data => {
        if (!data) return;
        getICD10DescriptionAndUpdateData(data, data => dispatch(reduxActions.loadData(data)));
      })
      .catch(() => {
        return;
      });
  };

const changePatientData =
  (data: Partial<IPatientUI> & { contactNotAllowed?: boolean | null }) =>
  async (dispatch: TDispatch): Promise<true | false> => {
    return await patchPatient(data)
      .then((res: boolean | null) => {
        if (res) {
          /*
           * The ICD-10 description is fetched every time patient data is edited, which is not optimal,
           * but still better than previously when it was fetched on every mount in several components.
           * Improve this at some point to fetch only if data is changed.
           */
          dispatch(reduxActions.changeData(data));
          if (typeof data.contactNotAllowed === 'boolean') {
            dispatch(reduxActions.changeContactNotAllowed(data.contactNotAllowed));
          }
          return true;
        }
        return false;
      })
      .catch(() => {
        return false;
      });
  };

const clearPatientData =
  () =>
  (dispatch: TDispatch): void => {
    dispatch(reduxActions.clearData());
  };

const changePatientExitusData =
  (data: TExitusUI) =>
  async (dispatch: TDispatch): Promise<true | false> => {
    return await patchPatientExitus(data)
      .then((res: boolean | null) => {
        if (res) {
          /*
           * The ICD-10 description is fetched every time patient data is edited, which is not optimal,
           * but still better than previously when it was fetched on every mount in several components.
           * Improve this at some point to fetch only if data is changed.
           */
          dispatch(reduxActions.changeExitus(data));
          return true;
        }
        return false;
      })
      .catch(() => {
        return false;
      });
  };

const loadGrantsData =
  () =>
  (dispatch: TDispatch): void => {
    fetchPatientGrants()
      .then(data => {
        if (!data) return;
        dispatch(reduxActions.loadGrants(data));
      })
      .catch(() => {
        dispatch(reduxActions.loadGrants([]));
      });
  };

const createNewGrant =
  (
    granteeOrg: TNeuroGrant['granteeOrg'],
    allow_write?: TNeuroGrant['allow_write'],
    allow_read?: TNeuroGrant['allow_read'],
  ) =>
  (dispatch: TDispatch): void => {
    const writeTypes = allow_write || [];
    const readTypes = allow_read || ['*'];
    createGrant(granteeOrg, writeTypes, readTypes)
      .then(data => {
        if (!data) return;
        dispatch(reduxActions.createGrant(data));
      })
      .catch(() => {
        return;
      });
  };

const updateExistingGrant =
  (
    id: TNeuroGrant['id'],
    data: {
      granteeGroup?: TNeuroGrant['granteeGroup'];
      granteeOrg?: TNeuroGrant['granteeOrg'];
      allow_write?: TNeuroGrant['allow_write'];
      allow_read?: TNeuroGrant['allow_read'];
    },
  ) =>
  (dispatch: TDispatch): void => {
    updateGrant(id, data)
      .then(data => {
        if (!data) return;
        dispatch(reduxActions.updateGrant(data));
      })
      .catch(() => {
        return;
      });
  };

const deleteExistingGrant =
  (id: TNeuroGrant['id']) =>
  (dispatch: TDispatch): void => {
    deleteGrant(id)
      .then(data => {
        if (!data) return;
        dispatch(reduxActions.deleteGrant(data));
      })
      .catch(() => {
        return;
      });
  };

const loadDelegates =
  () =>
  (dispatch: TDispatch): void => {
    fetchPatientDelegates()
      .then(data => {
        if (!data) return;
        dispatch(reduxActions.loadDelegates(data));
      })
      .catch(() => {
        dispatch(reduxActions.loadDelegates([]));
      });
  };

const loadContactInfo =
  () =>
  (dispatch: TDispatch): void => {
    fetchPatientContactInfo()
      .then(data => {
        if (!data) return;
        dispatch(reduxActions.loadContactInfo(data));
      })
      .catch(() => {
        dispatch(reduxActions.loadContactInfo({ contactNotAllowed: null, contacts: [] }));
      });
  };

const deleteSinglePatientContactInfo =
  (contactInfoId: string) =>
  async (dispatch: TDispatch): Promise<true | false> => {
    return await deletePatientContactInfo(contactInfoId)
      .then((res: string | null) => {
        if (res) {
          dispatch(reduxActions.deleteContactInfo(contactInfoId));
          return true;
        }
        return false;
      })
      .catch(() => {
        return false;
      });
  };

const createPatientContactInfo =
  (contactInfo: INeuroUIContactInfo) =>
  (dispatch: TDispatch): void => {
    postPatientContactInfo(contactInfo)
      .then(data => {
        if (!data) return;
        dispatch(reduxActions.createContactInfo(data));
      })
      .catch(() => {
        return;
      });
  };

const loadPatientInformants =
  () =>
  async (dispatch: TDispatch): Promise<void> => {
    fetchPatientInformants()
      .then(async data => {
        if (!data) return;
        const updatedData: Array<IInformant> = await Promise.all(
          data.map(async inf => {
            if (!inf.mysqUserId) return inf;
            const contacts = await fetchMyUserContactInfo(inf.mysqUserId ?? '');
            const userData = await fetchMyUserData(inf.mysqUserId ?? '');
            return {
              ...inf,
              email: contacts?.find(c => c.contactType === 'email')?.contactValue?.[0] || inf.email,
              phoneNumber: contacts?.find(c => c.contactType === 'sms')?.contactValue?.[0] || inf.phoneNumber,
              firstNames: userData?.firstNames || inf.firstNames,
              lastNames: userData?.lastNames || inf.lastNames,
            };
          }),
        );
        dispatch(reduxActions.loadInformants(updatedData));
      })
      .catch(() => {
        dispatch(reduxActions.loadInformants([]));
      });
  };

const createPatientInformant =
  (informant: Omit<IInformant, 'id'>) =>
  async (dispatch: TDispatch): Promise<string | null> => {
    return postPatientInformant(informant)
      .then(data => {
        if (!data) return null;
        // when response was 409 "informant already exists"
        if (typeof data === 'string') {
          return data;
        } else {
          dispatch(reduxActions.createInformant({ ...data, isActive: true }));
          return data.id;
        }
      })
      .catch(err => {
        return null;
      });
  };

const deleteInformant =
  (informantId: string, removedBy: string) =>
  async (dispatch: TDispatch): Promise<true | false> => {
    return await deletePatientInformant(informantId)
      .then((res: string | null) => {
        if (res) {
          dispatch(
            reduxActions.deleteInformant(informantId, {
              at: new Date().toISOString(),
              by: removedBy,
            }),
          );
          return true;
        }
        return false;
      })
      .catch(() => {
        return false;
      });
  };

const updatePatientInformant =
  (updatedInformantData: Partial<IInformant> & Pick<IInformant, 'id'>) =>
  async (dispatch: TDispatch): Promise<void> => {
    patchPatientInformant(updatedInformantData)
      .then(res => {
        if (!res) return;
        dispatch(reduxActions.updateInformant(updatedInformantData));
      })
      .catch(() => {
        return;
      });
  };

const linkPatToInformant = (informant: IInformant) => async (dispatch: TDispatch) => {
  linkPatientToInformant(informant.id)
    .then(res => {
      if (!res) return;
      dispatch(reduxActions.linkPatientToInformant(informant));
    })
    .catch(() => {
      return;
    });
};

const unlinkPatFromInformant = (infomantId: string) => async (dispatch: TDispatch) => {
  unlinkPatientFromInformant(infomantId)
    .then(res => {
      if (!res) return;
      dispatch(reduxActions.unlinkPatientFromInformant(infomantId));
    })
    .catch(() => {
      return;
    });
};

export const actions = {
  loadPatientData,
  changePatientData,
  clearPatientData,
  changePatientExitusData,
  loadGrantsData,
  createNewGrant,
  updateExistingGrant,
  deleteExistingGrant,
  loadDelegates,
  loadContactInfo,
  deleteSinglePatientContactInfo,
  createPatientContactInfo,
  loadPatientInformants,
  createPatientInformant,
  deleteInformant,
  updatePatientInformant,
  linkPatToInformant,
  unlinkPatFromInformant,
};
